import React, { HTMLAttributes, ReactElement, useEffect, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import clsx from "clsx";
import SelectableCollectionPagination from "./SelectableCollectionPagination";
import Spacer from "components/Spacer";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {},
        itemContainer: { display: "grid" },
        item: {
            gridColumn: 1,
            gridRow: 1,
            transition: theme.transitions.create(["opacity", "transform"], {
                duration: theme.transitions.duration.standard * 2,
            }),
            pointerEvents: "none",
        },
        showItem: {
            pointerEvents: "all",
        },
        fadeTransition: {
            opacity: 0,
        },
        showFadedItem: {
            opacity: 1,
        },
        offScreenItemPrevious: {
            transform: "translateX(-100vw)",
        },
        offScreenItemNext: {
            transform: "translateX(100vw)",
        },
        showOffscreenItem: {
            transform: "translateX(0vw)",
        },
    })
);

interface IProps<T> extends HTMLAttributes<HTMLDivElement> {
    items: T[];
    renderItem: (item: T) => ReactElement;
    autoTransitionDuration?: number;
    transition?: "fade" | "slide";
    disableArrows?: boolean;
}

export default function SelectableCollection<T>(props: IProps<T>) {
    const classes = useStyles();
    const {
        items,
        renderItem,
        autoTransitionDuration,
        transition = "fade",
        disableArrows = false,
        className,
        ...rest
    } = props;

    const [currentlySelectedItemIndex, setCurrentlySelectedItemIndex] = useState<number>(0);

    useEffect(() => {
        if (
            items.length <= 1 ||
            autoTransitionDuration === undefined ||
            autoTransitionDuration <= 0
        )
            return;

        const updateCurrentlySelectedIndex = () => {
            const nextIndex = (currentlySelectedItemIndex + 1) % items.length;

            setCurrentlySelectedItemIndex(nextIndex);
        };

        const interval = setInterval(() => {
            updateCurrentlySelectedIndex();
        }, autoTransitionDuration);

        return () => clearInterval(interval);
    }, [currentlySelectedItemIndex]);

    return (
        <div className={clsx(className, classes.root)} {...rest}>
            <div className={classes.itemContainer}>
                {items.map((item, i) => (
                    <div
                        className={clsx(
                            classes.item,
                            {
                                [classes.showItem]: i === currentlySelectedItemIndex,
                            },
                            transition === "fade" && {
                                [classes.fadeTransition]: true,
                                [classes.showFadedItem]: i === currentlySelectedItemIndex,
                            },
                            transition === "slide" && {
                                [classes.offScreenItemPrevious]: i < currentlySelectedItemIndex,
                                [classes.offScreenItemNext]: i > currentlySelectedItemIndex,
                                [classes.showOffscreenItem]: i === currentlySelectedItemIndex,
                            }
                        )}
                    >
                        {renderItem(item)}
                    </div>
                ))}
            </div>

            {items.length > 1 ? (
                <>
                    <Spacer />

                    <SelectableCollectionPagination
                        numberOfItems={items.length}
                        onPaginationClick={(itemIndex: number) =>
                            setCurrentlySelectedItemIndex(itemIndex)
                        }
                        currentlySelectedItemIndex={currentlySelectedItemIndex}
                        disableArrows={disableArrows}
                    />
                </>
            ) : null}
        </div>
    );
}
